import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import UserContext from "../context/UserContext";
import { db } from "../firebase";
import { collection, query, orderBy, where, getDocs, updateDoc, doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";

const SalesProspectingDashboard = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]);
  const [filteredProspects, setFilteredProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [postcodeAreas, setPostcodeAreas] = useState([]);
  // State for region configuration modal
  const [showRegionConfigModal, setShowRegionConfigModal] = useState(false);
  const [southPrefixes, setSouthPrefixes] = useState('');
  const [northPrefixes, setNorthPrefixes] = useState('');
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState(null);
  // State for postcode toggle list
  const [allPostcodePrefixes, setAllPostcodePrefixes] = useState([]);
  // State for prospect details modal
  const [showProspectDetailsModal, setShowProspectDetailsModal] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  // State for return to pool confirmation
  const [showReturnToPoolConfirm, setShowReturnToPoolConfirm] = useState(false);
  const [prospectToReturn, setProspectToReturn] = useState(null);
  // State for contact email editing
  const [editedContacts, setEditedContacts] = useState({});
  const [savingContact, setSavingContact] = useState(false);
  // State for contact role editing
  const [editedRoles, setEditedRoles] = useState({});
  const [savingRole, setSavingRole] = useState(false);
  // State for loading indicators
  const [generatingProspects, setGeneratingProspects] = useState(false);
  const [enrichingCompany, setEnrichingCompany] = useState(null);
  // State for contact deletion
  const [deletingContact, setDeletingContact] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  // State for adding new contact
  const [showAddContactForm, setShowAddContactForm] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [newContactRole, setNewContactRole] = useState('');
  const [addingContact, setAddingContact] = useState(false);
  // State for email template management
  const [showEmailTemplateModal, setShowEmailTemplateModal] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateSubject, setTemplateSubject] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  const [previewContact, setPreviewContact] = useState(null);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState(null);
  const [defaultTemplate, setDefaultTemplate] = useState(null);
  // State for filtering and sorting
  const [statusFilter, setStatusFilter] = useState(['new', 'emailed', 'qualified', 'disqualified', 'archived']);
  const [addedDateSort, setAddedDateSort] = useState('desc'); // 'asc' or 'desc'
  const [showStatusFilterDropdown, setShowStatusFilterDropdown] = useState(false);
  const [statusFilterPosition, setStatusFilterPosition] = useState({ top: 0, right: 0 });
  const statusFilterButtonRef = React.useRef(null);
  // State for postcode filter
  const [showPostcodeFilterDropdown, setShowPostcodeFilterDropdown] = useState(false);
  const [postcodeFilter, setPostcodeFilter] = useState([]);
  const [postcodeFilterPosition, setPostcodeFilterPosition] = useState({ top: 0, right: 0 });
  const postcodeFilterButtonRef = React.useRef(null);

  // Check if user is admin
  const isAdmin = user && user.email === "adamkfrancis1989@gmail.com";

  // Fetch daily prospects from Firestore
  useEffect(() => {
    if (!isAdmin) {
      // Skip fetching if not admin
      return;
    }

    const fetchProspects = async () => {
      try {
        setLoading(true);
        // Query 'daily_prospects' where status != 'archived', ordered by addedDate desc
        // Firestore doesn't allow inequality filters and sort orders on different fields
        // So we'll filter in the frontend instead
        const prospectsQuery = query(
          collection(db, 'daily_prospects'),
          orderBy('addedDate', 'desc')  // Only order by addedDate
        );

        const querySnapshot = await getDocs(prospectsQuery);
        const prospectsList = [];

        querySnapshot.forEach((doc) => {
          const prospectData = doc.data();
          // Filter out archived prospects in the frontend
          if (prospectData.status !== 'archived') {
            prospectsList.push({
              id: doc.id,
              ...prospectData
            });
          }
        });

        setProspects(prospectsList);

        // Extract unique postcode areas
        const uniqueAreas = [...new Set(prospectsList.map(p => p.postcodeArea).filter(area => area))].sort();
        setPostcodeAreas(uniqueAreas);
        // Initialize postcode filter with all areas
        setPostcodeFilter(uniqueAreas);

        // Set filtered prospects to all prospects initially
        setFilteredProspects(prospectsList);
        setError(null);
      } catch (err) {
        console.error('Error fetching prospects:', err);
        setError('Failed to load prospects. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProspects();
  }, [isAdmin]);

  // Filter and sort prospects when filters change
  useEffect(() => {
    if (prospects.length > 0) {
      let filtered = [...prospects];

      // Filter by postcode area (multi-select)
      if (postcodeFilter.length > 0) {
        filtered = filtered.filter(prospect =>
          prospect.postcodeArea && postcodeFilter.includes(prospect.postcodeArea)
        );
      }

      // Filter by status (multi-select)
      if (statusFilter.length > 0 && statusFilter.length < 5) {
        filtered = filtered.filter(prospect => statusFilter.includes(prospect.status));
      }

      // Sort by added date
      filtered.sort((a, b) => {
        const dateA = a.addedDate ? new Date(a.addedDate).getTime() : 0;
        const dateB = b.addedDate ? new Date(b.addedDate).getTime() : 0;
        return addedDateSort === 'asc' ? dateA - dateB : dateB - dateA;
      });

      setFilteredProspects(filtered);
    }
  }, [postcodeFilter, prospects, statusFilter, addedDateSort]);

  // Function to toggle postcode filter dropdown
  function togglePostcodeFilterDropdown() {
    if (showPostcodeFilterDropdown) {
      setShowPostcodeFilterDropdown(false);
    } else if (postcodeFilterButtonRef.current) {
      const rect = postcodeFilterButtonRef.current.getBoundingClientRect();
      setPostcodeFilterPosition({
        top: rect.bottom + window.scrollY + 5,
        right: window.innerWidth - rect.right
      });
      setShowPostcodeFilterDropdown(true);
    }
  }

  // Function to toggle postcode filter checkbox
  function togglePostcodeFilter(postcode) {
    setPostcodeFilter(prev => {
      if (prev.includes(postcode)) {
        return prev.filter(p => p !== postcode);
      } else {
        return [...prev, postcode];
      }
    });
  }

  // Function to select/deselect all postcodes
  function toggleSelectAllPostcodes() {
    if (postcodeFilter.length === postcodeAreas.length) {
      setPostcodeFilter([]);
    } else {
      setPostcodeFilter([...postcodeAreas]);
    }
  }

  // Function to toggle status filter dropdown
  function toggleStatusFilterDropdown() {
    if (showStatusFilterDropdown) {
      setShowStatusFilterDropdown(false);
    } else if (statusFilterButtonRef.current) {
      const rect = statusFilterButtonRef.current.getBoundingClientRect();
      setStatusFilterPosition({
        top: rect.bottom + window.scrollY + 5,
        right: window.innerWidth - rect.right
      });
      setShowStatusFilterDropdown(true);
    }
  }

  // Function to toggle status filter checkbox
  function toggleStatusFilter(status) {
    setStatusFilter(prev => {
      if (prev.includes(status)) {
        // Remove if already selected (but keep at least one)
        return prev.length > 1 ? prev.filter(s => s !== status) : prev;
      } else {
        // Add if not selected
        return [...prev, status];
      }
    });
  }

  // Function to select/deselect all statuses
  function toggleSelectAllStatuses() {
    if (statusFilter.length === 5) {
      // Deselect all except 'new'
      setStatusFilter(['new']);
    } else {
      // Select all
      setStatusFilter(['new', 'emailed', 'qualified', 'disqualified', 'archived']);
    }
  }

  // Reset edited contacts and roles when modal opens/closes
  useEffect(() => {
    if (!showProspectDetailsModal) {
      setEditedContacts({});
      setEditedRoles({});
    }
  }, [showProspectDetailsModal]);

  // Close status and postcode filter dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (showStatusFilterDropdown && !event.target.closest('[data-status-filter]')) {
        setShowStatusFilterDropdown(false);
      }
      if (showPostcodeFilterDropdown && !event.target.closest('[data-postcode-filter]')) {
        setShowPostcodeFilterDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStatusFilterDropdown, showPostcodeFilterDropdown]);

  // Function to format contact name for display (Title, First, Middle, Last order)
  function formatContactName(contact) {
    if (!contact.name) return '';
    
    const name = contact.name.trim();
    
    // Check if this is a Companies House officer (has appointed_on date)
    // These are stored as "Last, First Middle" or "Last, First Middle, Title"
    if (contact.isOfficer || contact.appointed_on) {
      if (name.includes(',')) {
        const parts = name.split(',');
        const lastName = parts[0].trim();
        
        // Check for title in the last part
        let firstAndMiddle = '';
        let title = '';
        
        if (parts.length >= 3) {
          const lastPart = parts[parts.length - 1].trim();
          const titles = ['Dr', 'Mr', 'Mrs', 'Ms', 'Miss', 'Sir', 'Dame', 'Lord', 'Lady', 'Prof', 'Professor', 'Hon', 'HRH'];
          
          if (titles.some(t => t.toLowerCase() === lastPart.toLowerCase().replace(/\./g, ''))) {
            title = lastPart;
            firstAndMiddle = parts.slice(1, parts.length - 1).join(',').trim();
          } else {
            firstAndMiddle = parts.slice(1).join(',').trim();
          }
        } else if (parts.length === 2) {
          firstAndMiddle = parts[1].trim();
        }
        
        // Split first and middle names
        const nameParts = firstAndMiddle.split(/\s+/).filter(p => p.length > 0);
        const firstName = nameParts[0] || '';
        const middleName = nameParts.slice(1).join(' ') || '';
        
        // Build formatted name: Title First Middle Last
        const formattedParts = [];
        if (title) formattedParts.push(title);
        if (firstName) formattedParts.push(firstName);
        if (middleName) formattedParts.push(middleName);
        if (lastName) formattedParts.push(lastName);
        
        return formattedParts.join(' ');
      }
    }
    
    // For non-officer contacts, return as-is (already in correct format)
    return formatText(name);
  }

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <div className="p-4 max-w-2xl mx-auto min-h-screen">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <h2 className="text-xl font-bold mb-2">Access Denied</h2>
          <p>You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  // Function to update prospect status
  const updateProspectStatus = async (prospectId, newStatus) => {
    try {
      const prospectRef = doc(db, 'daily_prospects', prospectId);
      await updateDoc(prospectRef, { status: newStatus });

      // Update local state
      setProspects(prevProspects =>
        prevProspects.map(prospect =>
          prospect.id === prospectId
            ? { ...prospect, status: newStatus }
            : prospect
        )
      );
    } catch (err) {
      console.error('Error updating prospect status:', err);
      setError('Failed to update prospect status. Please try again.');
    }
  };

  // Function to load region configuration
  const loadRegionConfiguration = async () => {
    try {
      const configDoc = await getDoc(doc(db, 'config', 'regions'));

      // Define all UK postcode prefixes
      const allPrefixes = [
        'AB', 'AL', 'B', 'BA', 'BB', 'BD', 'BH', 'BL', 'BN', 'BR',
        'BS', 'BT', 'CA', 'CB', 'CF', 'CH', 'CM', 'CO', 'CR', 'CT',
        'CV', 'CW', 'DA', 'DD', 'DE', 'DG', 'DH', 'DL', 'DN', 'DT',
        'DY', 'E', 'EC', 'EH', 'EN', 'EX', 'FK', 'FY', 'G', 'GL',
        'GU', 'HA', 'HD', 'HG', 'HP', 'HR', 'HS', 'HU', 'HX', 'IG',
        'IP', 'IV', 'KA', 'KT', 'KW', 'KY', 'L', 'LA', 'LD', 'LE',
        'LL', 'LN', 'LS', 'LU', 'M', 'ME', 'MK', 'ML', 'N', 'NE',
        'NG', 'NN', 'NP', 'NR', 'NW', 'OL', 'OX', 'PA', 'PE', 'PH',
        'PL', 'PO', 'PR', 'RG', 'RH', 'RM', 'S', 'SA', 'SE', 'SG', 'SK',
        'SL', 'SM', 'SN', 'SO', 'SP', 'SR', 'SS', 'ST', 'SW', 'SY',
        'TA', 'TD', 'TF', 'TN', 'TQ', 'TR', 'TS', 'TW', 'UB', 'W',
        'WA', 'WC', 'WD', 'WF', 'WN', 'WR', 'WS', 'WV', 'YO', 'ZE',
        'IM', 'GY', 'JE'
      ];

      if (configDoc.exists()) {
        const data = configDoc.data();
        const southArray = Array.isArray(data.southPrefixes) ? data.southPrefixes : [];
        const northArray = Array.isArray(data.northPrefixes) ? data.northPrefixes : [];
        
        // Create postcode list with region assignments
        const postcodeList = allPrefixes.map(prefix => ({
          prefix,
          region: southArray.includes(prefix) ? 'south' : northArray.includes(prefix) ? 'north' : 'south' // Default to south if not found
        }));
        
        setAllPostcodePrefixes(postcodeList);
        setSouthPrefixes(southArray.join(', '));
        setNorthPrefixes(northArray.join(', '));
      } else {
        // Create default config if it doesn't exist
        const defaultSouth = [
          'TR', 'PL', 'TQ', 'EX', 'TA', 'DT', 'BH', 'BS', 'BA', 'SP',
          'SN', 'SO', 'PO', 'RG', 'GU', 'RH', 'BN', 'HP', 'SL', 'LU',
          'AL', 'SG', 'CM', 'SS', 'CO', 'ME', 'CT', 'TN', 'SA', 'CF',
          'LD', 'NP', 'HR', 'GL', 'WR', 'CV', 'OX', 'NN', 'MK', 'TW',
          'KT', 'UB', 'HA', 'WD', 'EN', 'IG', 'RM', 'DA', 'BR', 'CR',
          'SM', 'SW', 'SE', 'EC', 'WC', 'W', 'NW', 'N'
        ];
        const defaultNorth = [
          'NE', 'DH', 'SR', 'TS', 'DL', 'HG', 'BD', 'LS', 'HX', 'WF',
          'BL', 'OL', 'SK', 'WA', 'CH', 'CW', 'ST', 'DE', 'LE', 'NG',
          'DN', 'S', 'HD', 'BB', 'LA', 'CA', 'HU', 'LN', 'YO', 'M'
        ];

        // Create postcode list with default region assignments
        const postcodeList = allPrefixes.map(prefix => ({
          prefix,
          region: defaultSouth.includes(prefix) ? 'south' : defaultNorth.includes(prefix) ? 'north' : 'south'
        }));
        
        setAllPostcodePrefixes(postcodeList);
        setSouthPrefixes(defaultSouth.join(', '));
        setNorthPrefixes(defaultNorth.join(', '));
      }
    } catch (error) {
      console.error('Error loading region configuration:', error);
      setConfigError(error.message);
    }
  };

  // Function to save region configuration
  const saveRegionConfiguration = async () => {
    try {
      setConfigLoading(true);
      setConfigError(null);

      // Build arrays from the toggle list
      const southArray = allPostcodePrefixes
        .filter(p => p.region === 'south')
        .map(p => p.prefix);
      
      const northArray = allPostcodePrefixes
        .filter(p => p.region === 'north')
        .map(p => p.prefix);

      // Save to Firestore
      await setDoc(doc(db, 'config', 'regions'), {
        southPrefixes: southArray,
        northPrefixes: northArray,
        updatedAt: new Date().toISOString()
      });

      alert('Region configuration saved successfully!');
      setShowRegionConfigModal(false);
    } catch (error) {
      console.error('Error saving region configuration:', error);
      setConfigError(error.message);
    } finally {
      setConfigLoading(false);
    }
  };

  // Function to toggle postcode region
  const togglePostcodeRegion = (prefix) => {
    setAllPostcodePrefixes(prev =>
      prev.map(p =>
        p.prefix === prefix
          ? { ...p, region: p.region === 'south' ? 'north' : 'south' }
          : p
      )
    );
  };

  // Open region config modal
  const openRegionConfigModal = () => {
    loadRegionConfiguration();
    setShowRegionConfigModal(true);
  };

  // Close region config modal
  const closeRegionConfigModal = () => {
    setShowRegionConfigModal(false);
    setConfigError(null);
  };

  // Function to open prospect details modal
  async function openProspectDetailsModal(prospect) {
    setSelectedProspect(prospect);
    setSelectedEmailTemplate(null);
    // Load email templates
    await loadEmailTemplates();
    setShowProspectDetailsModal(true);
  }

  // Function to close prospect details modal
  const closeProspectDetailsModal = () => {
    setShowProspectDetailsModal(false);
    setSelectedProspect(null);
  };

  // Helper function to format text with proper capitalization
  const formatText = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    // Convert to lowercase first, then capitalize the first letter of each word
    return text.toLowerCase().split(' ').map(word => {
      if (word.length > 0) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    }).join(' ');
  };

  // Move single prospect function
  async function handleMoveSingleProspect() {
    const region = document.getElementById('regionSelect')?.value;
    const quantity = document.getElementById('quantitySelect')?.value;

    if (!region) {
      alert('Please select a region');
      return;
    }

    if (!quantity) {
      alert('Please select a quantity');
      return;
    }

    try {
      setGeneratingProspects(true);

      // Call the test function to move multiple prospects by region
      // Using environment variables or fallback to known project ID
      const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'rockettrail-2705c';
      const functionRegion = process.env.REACT_APP_REGION || 'us-central1';

      const response = await fetch(`https://${functionRegion}-${projectId}.cloudfunctions.net/moveSingleProspect?region=${region}&quantity=${quantity}`, {
        method: 'GET',
      });

      // Check if response is OK before parsing JSON
      if (!response.ok) {
        // If response is not OK, try to get the error text
        let errorMessage;
        try {
          // Clone the response to allow multiple reads
          const clonedResponse = response.clone();
          const errorResult = await clonedResponse.json();
          errorMessage = errorResult.error || `HTTP error! status: ${response.status}`;
        } catch (e) {
          // If JSON parsing fails, get the raw text from another clone
          const clonedResponse = response.clone();
          errorMessage = await clonedResponse.text();
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.success) {
        alert(`${result.actualQuantity} prospects moved from ${region} region successfully!`);
        // Refresh the prospects list
        window.location.reload();
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Error moving prospects:', error);
      alert('Error moving prospects: ' + error.message);
    } finally {
      setGeneratingProspects(false);
    }
  }

  // Function to perform combined enrichment (Google Places + Companies House)
  async function handleEnrichProspect(companyNumber) {
    if (!companyNumber) {
      alert('Company number is required for enrichment');
      return;
    }

    try {
      setEnrichingCompany(companyNumber);

      // Call the combined enrichment function
      // Using environment variables or fallback to known project ID
      const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'rockettrail-2705c';
      const region = process.env.REACT_APP_REGION || 'us-central1';

      const response = await fetch(`https://${region}-${projectId}.cloudfunctions.net/enrichAndScrape?companyNumber=${companyNumber}`, {
        method: 'GET',
      });

      // Check if response is OK before parsing JSON
      if (!response.ok) {
        // If response is not OK, try to get the error text
        let errorMessage;
        try {
          // Clone the response to allow multiple reads
          const clonedResponse = response.clone();
          const errorResult = await clonedResponse.json();
          errorMessage = errorResult.error || `HTTP error! status: ${response.status}`;
        } catch (e) {
          // If JSON parsing fails, get the raw text from another clone
          const clonedResponse = response.clone();
          errorMessage = await clonedResponse.text();
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.success) {
        // Refresh the prospects list
        window.location.reload();
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Error in combined enrichment:', error);
      alert('Error in combined enrichment: ' + error.message);
    } finally {
      setEnrichingCompany(null);
    }
  }

  // Function to return a prospect from daily_prospects back to prospect_pool
  async function handleReturnToPool(prospectId, companyNumber) {
    if (!prospectId && !companyNumber) {
      alert('Prospect ID or company number is required');
      return;
    }

    try {
      const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'rockettrail-2705c';
      const region = process.env.REACT_APP_REGION || 'us-central1';

      const param = prospectId ? `prospectId=${prospectId}` : `companyNumber=${companyNumber}`;
      const response = await fetch(`https://${region}-${projectId}.cloudfunctions.net/returnProspectToPool?${param}`, {
        method: 'GET',
      });

      if (!response.ok) {
        let errorMessage;
        try {
          const clonedResponse = response.clone();
          const errorResult = await clonedResponse.json();
          errorMessage = errorResult.error || `HTTP error! status: ${response.status}`;
        } catch (e) {
          const clonedResponse = response.clone();
          errorMessage = await clonedResponse.text();
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.success) {
        alert(`Prospect "${result.companyName}" has been returned to the pool`);
        // Refresh the prospects list
        window.location.reload();
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Error returning prospect to pool:', error);
      alert('Error returning prospect to pool: ' + error.message);
    }
  }

  // Function to initiate return to pool (shows confirmation)
  function initiateReturnToPool(prospect) {
    setProspectToReturn(prospect);
    setShowReturnToPoolConfirm(true);
  }

  // Function to confirm and execute return to pool
  async function confirmReturnToPool() {
    if (prospectToReturn) {
      await handleReturnToPool(prospectToReturn.id, prospectToReturn.companyNumber);
      setShowReturnToPoolConfirm(false);
      setProspectToReturn(null);
    }
  }

  // Function to cancel return to pool
  function cancelReturnToPool() {
    setShowReturnToPoolConfirm(false);
    setProspectToReturn(null);
  }

  // Function to handle contact email change
  function handleContactEmailChange(contactIndex, newEmail) {
    setEditedContacts(prev => ({
      ...prev,
      [contactIndex]: newEmail
    }));
  }

  // Function to save contact email to Firestore
  async function saveContactEmail(contactIndex, newEmail) {
    if (!selectedProspect || !selectedProspect.contacts) return;

    try {
      setSavingContact(true);

      // Update the contacts array
      const updatedContacts = [...selectedProspect.contacts];
      updatedContacts[contactIndex] = {
        ...updatedContacts[contactIndex],
        email: newEmail
      };

      // Update Firestore
      const prospectRef = doc(db, 'daily_prospects', selectedProspect.id);
      await updateDoc(prospectRef, { contacts: updatedContacts });

      // Update local state
      setSelectedProspect({
        ...selectedProspect,
        contacts: updatedContacts
      });

      // Clear the edited state for this contact
      setEditedContacts(prev => {
        const newEdited = { ...prev };
        delete newEdited[contactIndex];
        return newEdited;
      });

      console.log('Contact email updated successfully');
    } catch (err) {
      console.error('Error updating contact email:', err);
      alert('Failed to update contact email. Please try again.');
    } finally {
      setSavingContact(false);
    }
  }

  // Function to reset edited email to original
  function resetContactEmail(contactIndex) {
    setEditedContacts(prev => {
      const newEdited = { ...prev };
      delete newEdited[contactIndex];
      return newEdited;
    });
  }

  // Function to handle contact role change
  function handleContactRoleChange(contactIndex, newRole) {
    setEditedRoles(prev => ({
      ...prev,
      [contactIndex]: newRole
    }));
  }

  // Function to save contact role to Firestore
  async function saveContactRole(contactIndex, newRole) {
    if (!selectedProspect || !selectedProspect.contacts) return;

    try {
      setSavingRole(true);

      // Update the contacts array
      const updatedContacts = [...selectedProspect.contacts];
      updatedContacts[contactIndex] = {
        ...updatedContacts[contactIndex],
        role: newRole
      };

      // Update Firestore
      const prospectRef = doc(db, 'daily_prospects', selectedProspect.id);
      await updateDoc(prospectRef, { contacts: updatedContacts });

      // Update local state
      setSelectedProspect({
        ...selectedProspect,
        contacts: updatedContacts
      });

      // Clear the edited state for this contact
      setEditedRoles(prev => {
        const newEdited = { ...prev };
        delete newEdited[contactIndex];
        return newEdited;
      });

      console.log('Contact role updated successfully');
    } catch (err) {
      console.error('Error updating contact role:', err);
      alert('Failed to update contact role. Please try again.');
    } finally {
      setSavingRole(false);
    }
  }

  // Function to reset edited role to original
  function resetContactRole(contactIndex) {
    setEditedRoles(prev => {
      const newEdited = { ...prev };
      delete newEdited[contactIndex];
      return newEdited;
    });
  }

  // Function to initiate contact deletion
  function initiateDeleteContact(contact, contactIndex) {
    setContactToDelete({ contact, contactIndex });
    setShowDeleteConfirm(true);
  }

  // Function to cancel contact deletion
  function cancelDeleteContact() {
    setShowDeleteConfirm(false);
    setContactToDelete(null);
  }

  // Function to confirm and execute contact deletion
  async function confirmDeleteContact() {
    if (!contactToDelete || !selectedProspect) return;

    try {
      setDeletingContact(contactToDelete.contactIndex);

      // Remove the contact from the contacts array
      const updatedContacts = selectedProspect.contacts.filter(
        (_, index) => index !== contactToDelete.contactIndex
      );

      // Update Firestore
      const prospectRef = doc(db, 'daily_prospects', selectedProspect.id);
      await updateDoc(prospectRef, { contacts: updatedContacts });

      // Update local state - also update the prospects list in the parent state
      setSelectedProspect({
        ...selectedProspect,
        contacts: updatedContacts
      });

      // Update the main prospects list to reflect the deletion
      setProspects(prevProspects =>
        prevProspects.map(prospect =>
          prospect.id === selectedProspect.id
            ? { ...prospect, contacts: updatedContacts }
            : prospect
        )
      );

      // Close the confirmation modal
      setShowDeleteConfirm(false);
      setContactToDelete(null);

      console.log('Contact deleted successfully');
    } catch (err) {
      console.error('Error deleting contact:', err);
      alert('Failed to delete contact. Please try again.');
    } finally {
      setDeletingContact(null);
    }
  }

  // Helper function to generate email from name using stored email format
  function generateEmailFromFormat(firstName, lastName, middleName, emailFormats, website) {
    if (!firstName || !lastName || !emailFormats || emailFormats.length === 0 || !website) {
      return '';
    }

    // Normalize names: lowercase and remove special characters
    const first = firstName.toLowerCase().replace(/[^a-z]/g, '');
    const last = lastName.toLowerCase().replace(/[^a-z]/g, '');
    const middle = middleName ? middleName.toLowerCase().replace(/[^a-z]/g, '') : '';

    if (!first || !last) return '';

    const f = first.charAt(0);
    const l = last.charAt(0);
    const m = middle ? middle.charAt(0) : '';

    // Use the first (most confident) email format
    const pattern = emailFormats[0].pattern;

    // Extract domain from website
    const domain = website.replace('www.', '').replace('http://', '').replace('https://', '').split('/')[0];

    let email = pattern.toLowerCase();

    // Replace domain placeholder if present
    email = email.replace(/@domain\.com/g, `@${domain}`).replace(/@domain/g, `@${domain}`);

    // Replace placeholders with actual name parts
    email = email
      .replace(/firstlast/g, `${first}${last}`)
      .replace(/first\.last/g, `${first}.${last}`)
      .replace(/first_last/g, `${first}_${last}`)
      .replace(/first-last/g, `${first}-${last}`)
      .replace(/firstmiddlelast/g, `${first}${middle}${last}`)
      .replace(/first\.middle\.last/g, `${first}.${middle}.${last}`)
      .replace(/flast/g, `${f}${last}`)
      .replace(/f\.last/g, `${f}.${last}`)
      .replace(/f_last/g, `${f}_${last}`)
      .replace(/f-last/g, `${f}-${last}`)
      .replace(/fmiddlelast/g, `${f}${middle}${last}`)
      .replace(/f\.middle\.last/g, `${f}.${middle}.${last}`)
      .replace(/first/g, first)
      .replace(/last/g, last)
      .replace(/middle/g, middle)
      .replace(/\bm\b/g, m);

    return email;
  }

  // Helper function to parse name into components
  function parseNameComponents(fullName) {
    if (!fullName || typeof fullName !== 'string') {
      return { firstName: '', lastName: '', middleName: '' };
    }

    const name = fullName.trim();
    const nameParts = name.split(/\s+/).filter(part => part.length > 0);

    if (nameParts.length === 0) {
      return { firstName: '', lastName: '', middleName: '' };
    } else if (nameParts.length === 1) {
      return { firstName: '', lastName: nameParts[0], middleName: '' };
    } else if (nameParts.length === 2) {
      return { firstName: nameParts[0], lastName: nameParts[1], middleName: '' };
    } else {
      const firstName = nameParts[0];
      const lastName = nameParts[nameParts.length - 1];
      const middleName = nameParts.slice(1, nameParts.length - 1).join(' ');
      return { firstName, lastName, middleName };
    }
  }

  // Function to show add contact form
  function showAddContact() {
    setShowAddContactForm(true);
    setNewContactName('');
    setNewContactRole('');
  }

  // Function to cancel add contact
  function cancelAddContact() {
    setShowAddContactForm(false);
    setNewContactName('');
    setNewContactRole('');
  }

  // Function to save new contact
  async function saveNewContact() {
    if (!newContactName.trim()) {
      alert('Please enter a contact name');
      return;
    }

    try {
      setAddingContact(true);

      // Parse the name to generate email
      const { firstName, lastName, middleName } = parseNameComponents(newContactName);

      // Generate email using stored email format
      let generatedEmail = '';
      if (selectedProspect.emailFormats && selectedProspect.emailFormats.length > 0 && selectedProspect.website) {
        generatedEmail = generateEmailFromFormat(
          firstName,
          lastName,
          middleName,
          selectedProspect.emailFormats,
          selectedProspect.website
        );
      }

      // Create new contact object
      const newContact = {
        name: newContactName.trim(),
        role: newContactRole.trim() || 'N/A',
        email: generatedEmail,
        phone: '',
        source_url: '',
        isOfficer: false
      };

      // Add to contacts array
      const updatedContacts = [...(selectedProspect.contacts || []), newContact];

      // Update Firestore
      const prospectRef = doc(db, 'daily_prospects', selectedProspect.id);
      await updateDoc(prospectRef, { contacts: updatedContacts });

      // Update local state
      setSelectedProspect({
        ...selectedProspect,
        contacts: updatedContacts
      });

      // Update the main prospects list
      setProspects(prevProspects =>
        prevProspects.map(prospect =>
          prospect.id === selectedProspect.id
            ? { ...prospect, contacts: updatedContacts }
            : prospect
        )
      );

      // Close the form
      setShowAddContactForm(false);
      setNewContactName('');
      setNewContactRole('');

      console.log('New contact added successfully');
    } catch (err) {
      console.error('Error adding contact:', err);
      alert('Failed to add contact. Please try again.');
    } finally {
      setAddingContact(false);
    }
  }

  // Function to load email templates from Firestore
  async function loadEmailTemplates() {
    try {
      setTemplateLoading(true);
      setTemplateError(null);

      const templatesQuery = query(collection(db, 'prospecting_emails'));
      const querySnapshot = await getDocs(templatesQuery);

      const templates = [];
      let foundDefault = null;
      
      querySnapshot.forEach((doc) => {
        const template = {
          id: doc.id,
          ...doc.data()
        };
        templates.push(template);
        
        // Check if this is the default template
        if (template.isDefault) {
          foundDefault = template;
        }
      });

      setEmailTemplates(templates);
      setDefaultTemplate(foundDefault);
      
      // Auto-select default template if exists
      if (foundDefault) {
        setSelectedEmailTemplate(foundDefault);
      }
    } catch (err) {
      console.error('Error loading email templates:', err);
      setTemplateError('Failed to load email templates');
    } finally {
      setTemplateLoading(false);
    }
  }

  // Function to open email template modal
  function openEmailTemplateModal() {
    loadEmailTemplates();
    setShowEmailTemplateModal(true);
  }

  // Function to close email template modal
  function closeEmailTemplateModal() {
    setShowEmailTemplateModal(false);
    setSelectedTemplate(null);
    setTemplateName('');
    setTemplateSubject('');
    setTemplateBody('');
    setPreviewContact(null);
    setTemplateError(null);
  }

  // Function to select a template for editing
  function selectTemplate(template) {
    setSelectedTemplate(template);
    setTemplateName(template.name || '');
    setTemplateSubject(template.subject || '');
    setTemplateBody(template.body || '');
  }

  // Function to create new template
  function createNewTemplate() {
    setSelectedTemplate(null);
    setTemplateName('');
    setTemplateSubject('');
    setTemplateBody('');
  }

  // Function to save email template
  async function saveEmailTemplate() {
    if (!templateName.trim()) {
      alert('Please enter a template name');
      return;
    }
    if (!templateSubject.trim()) {
      alert('Please enter a subject line');
      return;
    }
    if (!templateBody.trim()) {
      alert('Please enter an email body');
      return;
    }

    try {
      setTemplateLoading(true);

      const templateData = {
        name: templateName.trim(),
        subject: templateSubject.trim(),
        body: templateBody.trim(),
        updatedAt: new Date().toISOString()
      };

      if (selectedTemplate && selectedTemplate.id) {
        // Update existing template - preserve isDefault status
        templateData.isDefault = selectedTemplate.isDefault || false;
        const templateRef = doc(db, 'prospecting_emails', selectedTemplate.id);
        await updateDoc(templateRef, templateData);
      } else {
        // Create new template - not default by default
        templateData.createdAt = new Date().toISOString();
        templateData.isDefault = false;
        const templateRef = doc(collection(db, 'prospecting_emails'));
        await setDoc(templateRef, templateData);
      }

      // Reload templates
      await loadEmailTemplates();
      alert('Template saved successfully!');
    } catch (err) {
      console.error('Error saving email template:', err);
      setTemplateError('Failed to save template');
    } finally {
      setTemplateLoading(false);
    }
  }

  // Function to set template as default
  async function setTemplateAsDefault(templateId) {
    try {
      setTemplateLoading(true);

      // First, unset all other templates as default
      const unsetDefaultPromises = emailTemplates
        .filter(t => t.id !== templateId)
        .map(t => {
          const templateRef = doc(db, 'prospecting_emails', t.id);
          return updateDoc(templateRef, { isDefault: false });
        });

      await Promise.all(unsetDefaultPromises);

      // Then set the selected template as default
      const templateRef = doc(db, 'prospecting_emails', templateId);
      await updateDoc(templateRef, { isDefault: true });

      // Reload templates to get updated default
      await loadEmailTemplates();
    } catch (err) {
      console.error('Error setting default template:', err);
      setTemplateError('Failed to set default template');
    } finally {
      setTemplateLoading(false);
    }
  }

  // Function to remove default status from template
  async function removeDefaultTemplate(templateId) {
    try {
      const templateRef = doc(db, 'prospecting_emails', templateId);
      await updateDoc(templateRef, { isDefault: false });
      await loadEmailTemplates();
    } catch (err) {
      console.error('Error removing default template:', err);
      setTemplateError('Failed to remove default template');
    }
  }

  // Function to delete email template
  async function deleteEmailTemplate(templateId) {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const templateRef = doc(db, 'prospecting_emails', templateId);
      await deleteDoc(templateRef);
      await loadEmailTemplates();
      setSelectedTemplate(null);
      setTemplateName('');
      setTemplateSubject('');
      setTemplateBody('');
    } catch (err) {
      console.error('Error deleting template:', err);
      setTemplateError('Failed to delete template');
    }
  }

  // Function to log email sent date for a contact
  async function logEmailSent(contactIndex) {
    if (!selectedProspect || !selectedProspect.contacts) return;

    try {
      // Update the contacts array with email sent date
      const updatedContacts = [...selectedProspect.contacts];
      updatedContacts[contactIndex] = {
        ...updatedContacts[contactIndex],
        emailSentDate: new Date().toISOString()
      };

      // Check if all contacts with emails have been emailed
      const contactsWithEmail = updatedContacts.filter(c => c.email && c.email.trim() !== '');
      const contactsWithEmailSent = updatedContacts.filter(c => c.email && c.email.trim() !== '' && c.emailSentDate);
      const allContactsEmailed = contactsWithEmail.length > 0 && contactsWithEmail.length === contactsWithEmailSent.length;

      // Update prospect status to 'emailed' if all contacts have been emailed
      const newStatus = allContactsEmailed ? 'emailed' : selectedProspect.status;

      // Update Firestore
      const prospectRef = doc(db, 'daily_prospects', selectedProspect.id);
      await updateDoc(prospectRef, { 
        contacts: updatedContacts,
        status: newStatus
      });

      // Update local state
      const updatedProspect = {
        ...selectedProspect,
        contacts: updatedContacts,
        status: newStatus
      };
      setSelectedProspect(updatedProspect);

      // Update the main prospects list
      setProspects(prevProspects =>
        prevProspects.map(prospect =>
          prospect.id === selectedProspect.id
            ? { ...prospect, contacts: updatedContacts, status: newStatus }
            : prospect
        )
      );

      console.log('Email sent date logged successfully');
    } catch (err) {
      console.error('Error logging email sent date:', err);
      // Don't show alert - this is a background operation
    }
  }

  // Function to generate mailto link with template
  function generateMailtoLink(contactEmail, template, contact) {
    if (!contactEmail || !template) return `mailto:${contactEmail}`;

    // Format contact name properly (First Last format with normal capitalization)
    const contactName = formatContactName(contact || {});
    
    // Extract first name only (first word of the formatted name)
    const firstName = contactName.split(' ')[0] || '';
    
    // Get role with proper formatting
    const contactRole = formatText(contact?.role || 'N/A');
    
    // Format company name and address with proper capitalization
    const companyName = formatText(selectedProspect?.companyName || '');
    const companyAddress = formatText(selectedProspect?.siteAddress || '');

    // Replace placeholders in subject and body
    let subject = template.subject
      .replace(/{contactName}/g, contactName)
      .replace(/{firstName}/g, firstName)
      .replace(/{role}/g, contactRole)
      .replace(/{companyName}/g, companyName);

    let body = template.body
      .replace(/{contactName}/g, contactName)
      .replace(/{firstName}/g, firstName)
      .replace(/{role}/g, contactRole)
      .replace(/{companyName}/g, companyName)
      .replace(/{companyAddress}/g, companyAddress);

    // URL encode subject and body
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);

    return `mailto:${contactEmail}?subject=${encodedSubject}&body=${encodedBody}`;
  }

  // Function to preview email with contact
  function previewEmailWithContact(contact) {
    setPreviewContact(contact);
  }

  return (
    <div className="p-4 max-w-full mx-auto min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Sales Prospecting Dashboard</h1>
        <div className="flex space-x-2">
          <button
            onClick={openEmailTemplateModal}
            className="bg-indigo-500 text-white px-4 py-2 rounded hover:bg-indigo-600 font-semibold"
          >
            Email Templates
          </button>
          <button
            onClick={openRegionConfigModal}
            className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 font-semibold"
          >
            Configure Regions
          </button>
          <button
            onClick={() => navigate('/admin')}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 font-semibold"
          >
            Back to Admin
          </button>
        </div>
      </div>

      {/* Generate Prospects Section - Compact Card */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Generate Prospects</h2>
            <p className="text-xs text-gray-600 mt-0.5">Move prospects from pool to daily list</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              id="regionSelect"
              disabled={generatingProspects}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Select Region</option>
              <option value="south">South</option>
              <option value="north">North</option>
            </select>
            <select
              id="quantitySelect"
              disabled={generatingProspects}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Qty</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
              <option value="6">6</option>
              <option value="7">7</option>
              <option value="8">8</option>
              <option value="9">9</option>
              <option value="10">10</option>
            </select>
            <button
              onClick={handleMoveSingleProspect}
              disabled={generatingProspects}
              className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {generatingProspects ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </>
              ) : (
                'Generate'
              )}
            </button>
          </div>
        </div>
        {generatingProspects && (
          <div className="mt-3">
            <div className="flex items-center gap-2 text-xs text-blue-700 mb-1">
              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Generating prospects from Companies House...</span>
            </div>
            <div className="w-full bg-blue-100 rounded-full h-2 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full animate-pulse" style={{ width: '100%' }}></div>
            </div>
          </div>
        )}
      </div>

      {/* Daily Prospects Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-bold text-gray-800">Daily Prospects</h2>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 m-4 rounded-md">
            <p className="text-sm">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <p className="text-gray-500 text-sm">Loading prospects...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="py-3 px-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Map</th>
                  <th className="py-3 px-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Company Name</th>
                  <th className="py-3 px-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Site Address</th>
                  <th className="py-3 px-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <div className="relative inline-block text-left">
                      <button
                        ref={postcodeFilterButtonRef}
                        onClick={togglePostcodeFilterDropdown}
                        style={{ background: 'transparent', border: 'none', color: '#4B5563', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                        onMouseEnter={(e) => e.target.style.color = '#2563EB'}
                        onMouseLeave={(e) => e.target.style.color = '#4B5563'}
                        title="Filter by postcode"
                        data-postcode-filter="button"
                      >
                        <span>Postcode</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 transition-transform ${showPostcodeFilterDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showPostcodeFilterDropdown && (
                        <div
                          className="bg-white border border-gray-200 rounded shadow-xl z-[9999] p-2 max-h-96 overflow-y-auto"
                          style={{
                            position: 'fixed',
                            top: postcodeFilterPosition.top,
                            right: postcodeFilterPosition.right,
                            width: '12rem'
                          }}
                          data-postcode-filter="dropdown"
                        >
                          <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
                            <span className="text-xs font-semibold text-gray-700">Filter by Postcode</span>
                            <button
                              onClick={toggleSelectAllPostcodes}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              {postcodeFilter.length === postcodeAreas.length ? 'Deselect All' : 'Select All'}
                            </button>
                          </div>
                          <div className="space-y-1 max-h-64 overflow-y-auto">
                            {postcodeAreas.sort().map((postcode) => (
                              <label key={postcode} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5">
                                <input
                                  type="checkbox"
                                  checked={postcodeFilter.includes(postcode)}
                                  onChange={() => togglePostcodeFilter(postcode)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-3 w-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-xs text-gray-700">{postcode}</span>
                              </label>
                            ))}
                          </div>
                          <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500 text-center">
                            {postcodeFilter.length} selected
                          </div>
                        </div>
                      )}
                    </div>
                  </th>
                  <th className="py-3 px-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Company Number</th>
                  <th className="py-3 px-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Website</th>
                  <th className="py-3 px-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                  <th className="py-3 px-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Rating</th>
                  <th className="py-3 px-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-100">
                    <div className="relative inline-block text-left">
                      <button
                        ref={statusFilterButtonRef}
                        onClick={toggleStatusFilterDropdown}
                        style={{ background: 'transparent', border: 'none', color: '#4B5563', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                        onMouseEnter={(e) => e.target.style.color = '#2563EB'}
                        onMouseLeave={(e) => e.target.style.color = '#4B5563'}
                        title="Filter by status"
                        data-status-filter="button"
                      >
                        <span>Status</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 transition-transform ${showStatusFilterDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showStatusFilterDropdown && (
                        <div
                          className="bg-white border border-gray-200 rounded shadow-xl z-[9999] p-2"
                          style={{
                            position: 'fixed',
                            top: statusFilterPosition.top,
                            right: statusFilterPosition.right,
                            width: '12rem'
                          }}
                          data-status-filter="dropdown"
                        >
                          <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
                            <span className="text-xs font-semibold text-gray-700">Filter by Status</span>
                            <button
                              onClick={toggleSelectAllStatuses}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              {statusFilter.length === 5 ? 'Deselect All' : 'Select All'}
                            </button>
                          </div>
                          <div className="space-y-1">
                            {[
                              { value: 'new', label: 'New', color: 'bg-blue-200 text-blue-800' },
                              { value: 'emailed', label: 'Emailed', color: 'bg-purple-200 text-purple-800' },
                              { value: 'qualified', label: 'Qualified', color: 'bg-green-200 text-green-800' },
                              { value: 'disqualified', label: 'Disqualified', color: 'bg-red-200 text-red-800' },
                              { value: 'archived', label: 'Archived', color: 'bg-gray-200 text-gray-800' }
                            ].map(({ value, label, color }) => (
                              <label key={value} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5">
                                <input
                                  type="checkbox"
                                  checked={statusFilter.includes(value)}
                                  onChange={() => toggleStatusFilter(value)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-3 w-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className={`text-xs px-1.5 py-0.5 rounded ${color}`}>{label}</span>
                              </label>
                            ))}
                          </div>
                          <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500 text-center">
                            {statusFilter.length} selected
                          </div>
                        </div>
                      )}
                    </div>
                  </th>
                  <th className="py-3 px-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <button
                      onClick={() => setAddedDateSort(addedDateSort === 'asc' ? 'desc' : 'asc')}
                      style={{ background: 'transparent', border: 'none', color: '#4B5563', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                      onMouseEnter={(e) => e.target.style.color = '#2563EB'}
                      onMouseLeave={(e) => e.target.style.color = '#4B5563'}
                      title="Click to sort"
                    >
                      <span>Added Date</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 transition-transform ${addedDateSort === 'asc' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </th>
                  <th className="py-3 px-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Enrich/Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProspects.length > 0 ? (
                  filteredProspects.map((prospect) => (
                    <tr
                      key={prospect.id}
                      className="hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                      onClick={(e) => {
                        // Check if the click target is an interactive element that should not trigger the modal
                        const target = e.target;
                        const tagName = target.tagName.toLowerCase();
                        
                        // If the click is on an anchor tag, button, or select element, don't open the modal
                        if (tagName === 'a' || tagName === 'button' || tagName === 'select') {
                          return; // Do nothing, let the default action happen
                        }
                        
                        // Otherwise, open the modal
                        openProspectDetailsModal(prospect);
                      }}
                    >
                      <td className="py-3 px-2 text-center text-sm text-gray-700">
                        {prospect.googleMapsUrl ? (
                          <div className="flex items-center justify-center">
                            {(prospect.type && (prospect.type.toLowerCase() === 'factory')) && (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 7h3m11 4a2 2 0 01-2 2H3a2 2 0 01-2-2V5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1M8 7v8a2 2 0 002 2h2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                              </svg>
                            )}
                            {(prospect.type && (prospect.type.toLowerCase() === 'warehouse' || prospect.type.toLowerCase() === 'warehouses')) && (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            )}
                            <a
                              href={prospect.googleMapsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 inline-block flex-shrink-0"
                              title="View on Google Maps"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </a>
                          </div>
                        ) : 'N/A'}
                      </td>
                      <td className="py-3 px-3 text-left text-sm text-gray-700 truncate max-w-xs" title={prospect.companyName}>{prospect.companyName}</td>
                      <td className="py-3 px-3 text-left text-sm text-gray-700 truncate max-w-xs" title={formatText(prospect.siteAddress)}>{formatText(prospect.siteAddress)}</td>
                      <td className="py-3 px-2 text-center text-sm text-gray-700 truncate max-w-[80px]" title={prospect.postcode}>{prospect.postcode}</td>
                      <td className="py-3 px-2 text-center text-sm text-gray-700">
                        <div className="flex items-center justify-center">
                          <span className="truncate max-w-[60px]" title={prospect.companyNumber}>{prospect.companyNumber}</span>
                          <a
                            href={`https://find-and-update.company-information.service.gov.uk/company/${prospect.companyNumber}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 ml-1 flex-shrink-0"
                            title="View on Companies House"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-center text-sm text-gray-700">
                        {prospect.website ? (
                          <a
                            href={prospect.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 inline-block flex-shrink-0"
                            title="Visit Website"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                            </svg>
                          </a>
                        ) : <span className="text-gray-400">N/A</span>}
                      </td>
                      <td className="py-3 px-2 text-center text-sm text-gray-700 truncate max-w-[100px]" title={prospect.phone || 'N/A'}>
                        {prospect.phone || <span className="text-gray-400">N/A</span>}
                      </td>
                      <td className="py-3 px-2 text-center text-sm text-gray-700 truncate max-w-[80px]" title={prospect.rating ? `${prospect.rating}/5 (${prospect.userRatingsTotal || 0})` : 'N/A'}>
                        {prospect.rating ? `${prospect.rating}/5 (${prospect.userRatingsTotal || 0})` : <span className="text-gray-400">N/A</span>}
                      </td>
                      <td className="py-3 px-2 text-center text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          prospect.status === 'new' ? 'bg-blue-100 text-blue-700' :
                          prospect.status === 'emailed' ? 'bg-purple-100 text-purple-700' :
                          prospect.status === 'qualified' ? 'bg-green-100 text-green-700' :
                          prospect.status === 'disqualified' ? 'bg-red-100 text-red-700' :
                          prospect.status === 'archived' ? 'bg-gray-100 text-gray-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {prospect.status}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center text-sm text-gray-700 truncate max-w-[100px]" title={prospect.addedDate ? new Date(prospect.addedDate).toLocaleDateString() : 'N/A'}>
                        {prospect.addedDate ? new Date(prospect.addedDate).toLocaleDateString() : <span className="text-gray-400">N/A</span>}
                      </td>
                      <td className="py-3 px-2 text-center text-sm">
                        <div className="relative">
                          {enrichingCompany === prospect.companyNumber ? (
                            <div className="flex items-center justify-center gap-2 text-xs text-blue-600">
                              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span>Enriching...</span>
                            </div>
                          ) : (
                            <select
                              value=""
                              onChange={(e) => {
                                const action = e.target.value;
                                if (action === 'enrich') {
                                  handleEnrichProspect(prospect.companyNumber);
                                } else if (action === 'qualified') {
                                  updateProspectStatus(prospect.id, 'qualified');
                                } else if (action === 'disqualified') {
                                  updateProspectStatus(prospect.id, 'disqualified');
                                } else if (action === 'archived') {
                                  updateProspectStatus(prospect.id, 'archived');
                                } else if (action === 'emailed') {
                                  updateProspectStatus(prospect.id, 'emailed');
                                } else if (action === 'returnToPool') {
                                  initiateReturnToPool(prospect);
                                }
                                e.target.value = '';
                              }}
                              className="px-2 py-1 bg-white text-gray-700 text-xs rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer hover:bg-gray-50 transition-colors"
                            >
                              <option value="" disabled>Select Action</option>
                              <option value="enrich">Enrich</option>
                              <option value="emailed">Mark as Emailed</option>
                              <option value="qualified">Qualify</option>
                              <option value="disqualified">Disqualify</option>
                              <option value="archived">Archive</option>
                              <option value="returnToPool">Return to Pool</option>
                            </select>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="py-8 px-4 text-center text-gray-500 text-sm">
                      <div className="flex flex-col items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p>No prospects found matching the selected filter.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showRegionConfigModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-start mb-4 pb-3 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Region Configuration</h2>
                <p className="text-sm text-gray-600 mt-1">Assign each postcode prefix to North or South region</p>
              </div>
              <button
                onClick={closeRegionConfigModal}
                className="text-gray-400 hover:text-gray-600 text-2xl transition-colors"
              >
                &times;
              </button>
            </div>

            {configError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <p>Error: {configError}</p>
              </div>
            )}

            {/* Stats Bar */}
            <div className="flex gap-4 mb-4 pb-4 border-b border-gray-200">
              <div className="flex-1 bg-gradient-to-br from-blue-50 to-indigo-50 p-3 rounded-lg border border-blue-200">
                <div className="text-2xl font-bold text-blue-700">
                  {allPostcodePrefixes.filter(p => p.region === 'south').length}
                </div>
                <div className="text-xs text-blue-600 uppercase tracking-wide mt-0.5">South Postcodes</div>
              </div>
              <div className="flex-1 bg-gradient-to-br from-orange-50 to-red-50 p-3 rounded-lg border border-orange-200">
                <div className="text-2xl font-bold text-orange-700">
                  {allPostcodePrefixes.filter(p => p.region === 'north').length}
                </div>
                <div className="text-xs text-orange-600 uppercase tracking-wide mt-0.5">North Postcodes</div>
              </div>
              <div className="flex-1 bg-gradient-to-br from-gray-50 to-slate-50 p-3 rounded-lg border border-gray-200">
                <div className="text-2xl font-bold text-gray-700">
                  {allPostcodePrefixes.length}
                </div>
                <div className="text-xs text-gray-600 uppercase tracking-wide mt-0.5">Total Postcodes</div>
              </div>
            </div>

            {/* Postcode Grid */}
            <div className="flex-1 overflow-y-auto mb-4">
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {allPostcodePrefixes.sort((a, b) => a.prefix.localeCompare(b.prefix)).map((postcode) => (
                  <div
                    key={postcode.prefix}
                    className={`flex items-center justify-between p-2 rounded-lg border transition-all ${
                      postcode.region === 'south'
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-orange-50 border-orange-200'
                    }`}
                  >
                    <span className="text-sm font-semibold text-gray-700">{postcode.prefix}</span>
                    <button
                      onClick={() => togglePostcodeRegion(postcode.prefix)}
                      className={`relative inline-flex h-6 w-14 items-center rounded-full transition-colors ${
                        postcode.region === 'south' ? 'bg-blue-500' : 'bg-orange-500'
                      }`}
                      title={`Click to switch to ${postcode.region === 'south' ? 'North' : 'South'}`}
                    >
                      <span
                        className={`absolute h-4 w-4 rounded-full bg-white transition-all duration-200 ${
                          postcode.region === 'south' ? 'left-0.5' : 'left-9'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
              <div className="text-xs text-gray-500">
                <span className="inline-flex items-center mr-3">
                  <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-1"></span> South
                </span>
                <span className="inline-flex items-center">
                  <span className="inline-block w-3 h-3 bg-orange-500 rounded-full mr-1"></span> North
                </span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={closeRegionConfigModal}
                  className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 font-medium transition-colors"
                  disabled={configLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={saveRegionConfiguration}
                  className="bg-blue-600 text-white px-5 py-2 rounded-md hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  disabled={configLoading}
                >
                  {configLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save Configuration'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Prospect Details Modal */}
      {showProspectDetailsModal && selectedProspect && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-5 pb-4 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {formatText(selectedProspect.companyName)}
                </h2>
                <p className="text-sm text-gray-500 mt-1">{selectedProspect.companyNumber}</p>
              </div>
              <button
                onClick={closeProspectDetailsModal}
                className="text-gray-400 hover:text-gray-600 text-2xl transition-colors"
              >
                &times;
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-800">Contact Information</h3>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{selectedProspect.contacts?.length || 0} contacts</span>
              </div>

              {/* Email Template Selector */}
              <div className="mb-4 p-3 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-100">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Email Template</label>
                  {defaultTemplate && (
                    <span className="text-xs text-green-600 flex items-center bg-green-50 px-2 py-0.5 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Default selected
                    </span>
                  )}
                </div>
                <select
                  value={selectedEmailTemplate?.id || ''}
                  onChange={(e) => {
                    const template = emailTemplates.find(t => t.id === e.target.value);
                    setSelectedEmailTemplate(template || null);
                  }}
                  className="w-full p-2.5 border border-gray-300 rounded-md text-gray-900 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">No template (basic mailto)</option>
                  {emailTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}{template.isDefault ? ' (Default)' : ''}
                    </option>
                  ))}
                </select>
                {selectedEmailTemplate && (
                  <div className="mt-2 text-xs text-gray-600 bg-white px-2 py-1.5 rounded border border-gray-200">
                    <span className="font-medium text-gray-700">Subject:</span> {selectedEmailTemplate.subject}
                  </div>
                )}
              </div>

              {/* Display contacts if available */}
              {selectedProspect.contacts && selectedProspect.contacts.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-gray-700 mb-2">Contacts ({selectedProspect.contacts.length}):</h4>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {selectedProspect.contacts.map((contact, index) => {
                      const currentEmail = editedContacts[index] !== undefined ? editedContacts[index] : (contact.email || '');
                      const isEdited = editedContacts[index] !== undefined;
                      const originalEmail = contact.email || '';

                      return (
                        <div key={index} className="text-gray-700 bg-gray-50 p-3 rounded border border-gray-200">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              {/* Display formatted contact name */}
                              <div className="font-semibold text-gray-900">{formatContactName(contact)}</div>
                              {/* Editable email field */}
                              <div className="flex items-center space-x-2 mt-1">
                                <input
                                  type="email"
                                  value={currentEmail}
                                  onChange={(e) => handleContactEmailChange(index, e.target.value)}
                                  onBlur={() => {
                                    if (currentEmail !== originalEmail) {
                                      saveContactEmail(index, currentEmail);
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.target.blur();
                                    } else if (e.key === 'Escape') {
                                      resetContactEmail(index);
                                    }
                                  }}
                                  className="text-sm text-blue-600 bg-white border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500 w-72"
                                  placeholder="Enter email address"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                {isEdited && (
                                  <span className="text-xs text-orange-600 flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    Unsaved
                                  </span>
                                )}
                                {savingContact && (
                                  <span className="text-xs text-blue-600">Saving...</span>
                                )}
                              </div>
                              {/* Editable role field */}
                              <div className="flex items-center space-x-2 mt-1">
                                <input
                                  type="text"
                                  value={editedRoles[index] !== undefined ? editedRoles[index] : (contact.role || '')}
                                  onChange={(e) => handleContactRoleChange(index, e.target.value)}
                                  onBlur={() => {
                                    const currentRole = editedRoles[index] !== undefined ? editedRoles[index] : (contact.role || '');
                                    const originalRole = contact.role || '';
                                    if (currentRole !== originalRole) {
                                      saveContactRole(index, currentRole);
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.target.blur();
                                    } else if (e.key === 'Escape') {
                                      resetContactRole(index);
                                    }
                                  }}
                                  className="text-sm text-gray-700 bg-white border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 w-72"
                                  placeholder="Enter role/title"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                {editedRoles[index] !== undefined && (
                                  <span className="text-xs text-orange-600 flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    Unsaved
                                  </span>
                                )}
                                {savingRole && (
                                  <span className="text-xs text-blue-600">Saving...</span>
                                )}
                              </div>
                              {contact.appointed_on && (
                                <div className="text-xs text-gray-500">Appointed: {contact.appointed_on}</div>
                              )}
                            </div>
                            <div className="flex flex-col items-end space-y-1 ml-4">
                              {currentEmail && (
                                <div className="flex flex-col items-end">
                                  <a
                                    href={generateMailtoLink(currentEmail, selectedEmailTemplate, contact)}
                                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      logEmailSent(index);
                                    }}
                                    title={selectedEmailTemplate ? 'Email with template' : 'Email contact'}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                  </a>
                                  {contact.emailSentDate && (
                                    <div className="text-xs text-green-600 mt-1 flex items-center">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                      {new Date(contact.emailSentDate).toLocaleDateString('en-GB', { 
                                        day: 'numeric', 
                                        month: 'short',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                              {contact.phone && (
                                <a
                                  href={`tel:${contact.phone}`}
                                  className="text-green-600 hover:text-green-800 text-sm flex items-center"
                                  onClick={(e) => e.stopPropagation()}
                                  title="Call contact"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1M8 7v8a2 2 0 002 2h2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                                  </svg>
                                </a>
                              )}
                              {contact.source_url && (
                                <a
                                  href={contact.source_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-purple-600 hover:text-purple-800 text-sm flex items-center"
                                  onClick={(e) => e.stopPropagation()}
                                  title="Search for contact"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                  </svg>
                                </a>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  initiateDeleteContact(contact, index);
                                }}
                                disabled={deletingContact === index}
                                className="text-red-600 hover:text-red-800 text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Delete contact"
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex' }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" stroke="currentColor" stroke="red" fill="none">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Add Contact Button */}
              <div className="mb-4">
                <button
                  onClick={showAddContact}
                  className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 text-sm font-medium flex items-center shadow-sm transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add New Contact
                </button>
              </div>

              {/* Add Contact Form */}
              {showAddContactForm && (
                <div className="mb-4 bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                    Add New Contact
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-green-700 mb-1">Name *</label>
                      <input
                        type="text"
                        value={newContactName}
                        onChange={(e) => setNewContactName(e.target.value)}
                        className="w-full p-2.5 border border-green-300 rounded-md text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="e.g., Darren Evans"
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-green-700 mb-1">Role / Title</label>
                      <input
                        type="text"
                        value={newContactRole}
                        onChange={(e) => setNewContactRole(e.target.value)}
                        className="w-full p-2.5 border border-green-300 rounded-md text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="e.g., Health & Safety Manager"
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                    </div>
                    {selectedProspect.emailFormats && selectedProspect.emailFormats.length > 0 && (
                      <div className="text-xs text-green-700 bg-white px-3 py-2 rounded border border-green-200">
                        <span className="font-medium">Email format:</span> <span className="font-mono text-green-600">{selectedProspect.emailFormats[0].pattern}</span>
                      </div>
                    )}
                    <div className="flex justify-end space-x-2 pt-2">
                      <button
                        onClick={cancelAddContact}
                        className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 text-sm font-medium transition-colors"
                        disabled={addingContact}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveNewContact}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        disabled={addingContact || !newContactName.trim()}
                      >
                        {addingContact ? 'Adding...' : 'Add Contact'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Company Details Section */}
              <div className="mt-6 pt-5 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Company Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Left Column - Address */}
                  <div className="bg-gradient-to-br from-gray-50 to-slate-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-start mb-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Site Address</p>
                        <div className="text-sm text-gray-700 space-y-0.5">
                          {(() => {
                            // Use sites array if available, otherwise fall back to siteAddress
                            const addressSource = selectedProspect.sites && selectedProspect.sites.length > 0 
                              ? selectedProspect.sites[0] 
                              : formatText(selectedProspect.siteAddress);
                            // Split by comma if available, otherwise show as single line
                            return addressSource.includes(',') 
                              ? addressSource.split(',').map((line, idx) => (
                                  <p key={idx} className="leading-relaxed">{line.trim()}</p>
                                ))
                              : <p className="leading-relaxed">{addressSource}</p>;
                          })()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start mt-3 pt-3 border-t border-gray-200">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                      </svg>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Postcode</p>
                        <p className="text-sm font-mono text-gray-700 bg-white px-2 py-1 rounded border border-gray-200 inline-block">{selectedProspect.postcode}</p>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Contact Info */}
                  <div className="bg-gradient-to-br from-gray-50 to-slate-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-start mb-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1M8 7v8a2 2 0 002 2h2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                      </svg>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Phone</p>
                        {selectedProspect.phone ? (
                          <a href={`tel:${selectedProspect.phone}`} className="text-sm text-blue-600 hover:text-blue-800 hover:underline">{selectedProspect.phone}</a>
                        ) : (
                          <p className="text-sm text-gray-400">Not available</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start mb-3 pt-3 border-t border-gray-200">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Website</p>
                        {selectedProspect.website ? (
                          <a href={selectedProspect.website} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 hover:underline break-all">{selectedProspect.website.replace('https://', '').replace('http://', '')}</a>
                        ) : (
                          <p className="text-sm text-gray-400">Not available</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start pt-3 border-t border-gray-200">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                      </svg>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Company Number</p>
                        <a
                          href={`https://find-and-update.company-information.service.gov.uk/company/${selectedProspect.companyNumber}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-mono"
                        >
                          {selectedProspect.companyNumber}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="mt-5 flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-600 mr-2">Status:</span>
                    <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                      selectedProspect.status === 'new' ? 'bg-blue-100 text-blue-700' :
                      selectedProspect.status === 'emailed' ? 'bg-purple-100 text-purple-700' :
                      selectedProspect.status === 'qualified' ? 'bg-green-100 text-green-700' :
                      selectedProspect.status === 'disqualified' ? 'bg-red-100 text-red-700' :
                      selectedProspect.status === 'archived' ? 'bg-gray-100 text-gray-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {selectedProspect.status}
                    </span>
                  </div>
                  <button
                    onClick={closeProspectDetailsModal}
                    className="bg-gray-100 text-gray-700 px-5 py-2 rounded-md hover:bg-gray-200 text-sm font-medium transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Return to Pool Confirmation Modal */}
      {showReturnToPoolConfirm && prospectToReturn && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 text-gray-800">Return to Pool</h2>
            <p className="text-gray-700 mb-6">
              Are you sure you want to return <span className="font-semibold">{formatText(prospectToReturn.companyName)}</span> to the prospect pool?
            </p>
            <p className="text-gray-600 text-sm mb-6">
              This will remove it from daily prospects and reset its status to 'unprocessed', allowing you to enrich it again.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={cancelReturnToPool}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={confirmReturnToPool}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Return to Pool
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Contact Confirmation Modal */}
      {showDeleteConfirm && contactToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 text-gray-800">Delete Contact</h2>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete <span className="font-semibold">{formatContactName(contactToDelete.contact)}</span> from this prospect?
            </p>
            <p className="text-gray-600 text-sm mb-6">
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={cancelDeleteContact}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                disabled={deletingContact !== null}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteContact}
                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={deletingContact !== null}
              >
                {deletingContact !== null ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Template Modal */}
      {showEmailTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold text-gray-800">Email Templates</h2>
              <button
                onClick={closeEmailTemplateModal}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                &times;
              </button>
            </div>

            {templateError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <p>{templateError}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Templates List */}
              <div className="md:col-span-1">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold text-gray-800">Saved Templates</h3>
                  <button
                    onClick={createNewTemplate}
                    className="bg-indigo-500 text-white px-2 py-1 rounded text-xs hover:bg-indigo-600"
                  >
                    + New Template
                  </button>
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded p-2">
                  {templateLoading && emailTemplates.length === 0 ? (
                    <p className="text-gray-500 text-sm">Loading...</p>
                  ) : emailTemplates.length === 0 ? (
                    <p className="text-gray-500 text-sm">No templates yet. Create your first one!</p>
                  ) : (
                    emailTemplates.map((template) => (
                      <div
                        key={template.id}
                        onClick={() => selectTemplate(template)}
                        className={`p-2 rounded cursor-pointer text-sm ${
                          selectedTemplate?.id === template.id
                            ? 'bg-indigo-100 border-indigo-300 border'
                            : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-1">
                              <span className="font-medium text-gray-900 truncate">{template.name}</span>
                              {template.isDefault && (
                                <span className="text-xs bg-green-100 text-green-700 px-1 rounded flex-shrink-0">Default</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 truncate">{template.subject}</div>
                          </div>
                          <div className="flex-shrink-0 ml-2">
                            {!template.isDefault ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTemplateAsDefault(template.id);
                                }}
                                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium whitespace-nowrap"
                                title="Set as default"
                              >
                                Set as default
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeDefaultTemplate(template.id);
                                }}
                                className="text-xs text-gray-500 hover:text-gray-700 font-medium whitespace-nowrap"
                                title="Remove default"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Template Editor */}
              <div className="md:col-span-2">
                <h3 className="font-semibold text-gray-800 mb-2">
                  {selectedTemplate ? 'Edit Template' : 'Create New Template'}
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Template Name *</label>
                    <input
                      type="text"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded text-gray-900 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      placeholder="e.g., Initial Outreach"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Subject Line *</label>
                    <input
                      type="text"
                      value={templateSubject}
                      onChange={(e) => setTemplateSubject(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded text-gray-900 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      placeholder="e.g., Quick question about {companyName}'s safety procedures"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Available placeholders: <span className="font-mono">{`{contactName}`}</span>, <span className="font-mono">{`{firstName}`}</span>, <span className="font-mono">{`{role}`}</span>, <span className="font-mono">{`{companyName}`}</span>
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Email Body *</label>
                    <textarea
                      value={templateBody}
                      onChange={(e) => setTemplateBody(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded text-gray-900 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 h-48 font-mono text-sm"
                      placeholder={`Hi {contactName},\n\nI hope this email finds you well...\n\nBest regards`}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Available placeholders: <span className="font-mono">{`{contactName}`}</span>, <span className="font-mono">{`{firstName}`}</span>, <span className="font-mono">{`{role}`}</span>, <span className="font-mono">{`{companyName}`}</span>, <span className="font-mono">{`{companyAddress}`}</span>
                    </p>
                  </div>
                  <div className="flex justify-between items-center">
                    {selectedTemplate && (
                      <button
                        onClick={() => deleteEmailTemplate(selectedTemplate.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Delete Template
                      </button>
                    )}
                    <div className="flex space-x-2 ml-auto">
                      <button
                        onClick={closeEmailTemplateModal}
                        className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                        disabled={templateLoading}
                      >
                        Close
                      </button>
                      <button
                        onClick={saveEmailTemplate}
                        className="bg-indigo-500 text-white px-4 py-2 rounded hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={templateLoading}
                      >
                        {templateLoading ? 'Saving...' : 'Save Template'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Preview Section */}
            {selectedTemplate && (
              <div className="mt-6 border-t pt-4">
                <h3 className="font-semibold text-gray-800 mb-2">Preview</h3>
                <div className="bg-gray-50 p-4 rounded border border-gray-200">
                  <div className="mb-2">
                    <span className="text-sm text-gray-600">Subject: </span>
                    <span className="text-sm font-medium text-gray-900">
                      {templateSubject
                        .replace(/{contactName}/g, 'John Smith')
                        .replace(/{firstName}/g, 'John')
                        .replace(/{role}/g, 'Health & Safety Manager')
                        .replace(/{companyName}/g, 'Example Ltd')}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
                    {templateBody
                      .replace(/{contactName}/g, 'John Smith')
                      .replace(/{firstName}/g, 'John')
                      .replace(/{role}/g, 'Health & Safety Manager')
                      .replace(/{companyName}/g, 'Example Ltd')
                      .replace(/{companyAddress}/g, '123 Example Street, London SW1A 1AA')}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SalesProspectingDashboard;